import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TooltipModule } from 'primeng/tooltip';

export interface SearchQuery {
  filter: string;
  deep: boolean;
}

@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [FormsModule, InputTextModule, IconFieldModule, InputIconModule, ToggleButtonModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="search-bar__wrapper">
      @if (showFileBrowserButton) {
        <button
          type="button"
          class="search-bar__file-browser-btn"
          [class.search-bar__file-browser-btn--active]="fileBrowserActive"
          [attr.aria-pressed]="fileBrowserActive"
          [pTooltip]="fileBrowserActive ? 'Dokumentummátrix megjelenítése' : 'Fájlkezelő megnyitása a bal oldalon'"
          tooltipPosition="bottom"
          (click)="fileBrowserToggle.emit()"
        >
          <i class="pi pi-folder"></i>
        </button>
      }
      <p-iconfield class="search-bar__field">
        <p-inputicon class="pi pi-search" />
        <input
          type="text"
          pInputText
          [placeholder]="placeholder"
          [(ngModel)]="searchText"
          class="search-bar__input"
          (keyup.enter)="submit()"
          (input)="onInput()"
        />
        @if (searchText) {
          <p-inputicon class="pi pi-times search-bar__clear" (click)="clear()" />
        }
      </p-iconfield>
      <p-togglebutton
        [(ngModel)]="deep"
        onIcon="pi pi-sitemap"
        offIcon="pi pi-sitemap"
        onLabel=""
        offLabel=""
        class="search-bar__deep-toggle"
        [pTooltip]="deep ? 'Almappákban is keres' : 'Csak aktuális mappa'"
        tooltipPosition="bottom"
        (onChange)="onDeepChange()"
      />
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .search-bar__wrapper { display: flex; gap: 4px; align-items: center; width: 100%; }
    .search-bar__field { flex: 1; }
    :host ::ng-deep .p-iconfield { width: 100%; }
    :host ::ng-deep .search-bar__input.p-inputtext {
      width: 100%;
      font-size: 13px;
      padding: 0.5rem 0.75rem 0.5rem 2.2rem;
      border: 1px solid var(--fm-border);
      border-radius: 4px;
    }
    :host ::ng-deep .search-bar__input.p-inputtext:focus {
      border-color: var(--fm-primary);
      box-shadow: 0 0 0 2px rgba(26, 58, 108, 0.1);
    }
    :host ::ng-deep .search-bar__clear { cursor: pointer; }
    :host ::ng-deep .search-bar__clear:hover { color: var(--fm-primary); }
    :host ::ng-deep .search-bar__deep-toggle.p-togglebutton {
      width: 2.2rem;
      height: 2.2rem;
      padding: 0;
      flex-shrink: 0;
    }
    :host ::ng-deep .search-bar__deep-toggle .p-togglebutton-content {
      gap: 0;
      padding: 0;
    }
    :host ::ng-deep .search-bar__deep-toggle .p-togglebutton-label {
      display: none;
    }
    .search-bar__file-browser-btn {
      width: 2.6rem;
      height: 2.6rem;
      padding: 0;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--fm-border);
      border-radius: 4px;
      background: #fff;
      color: var(--fm-text, #374151);
      cursor: pointer;
    }
    .search-bar__file-browser-btn .pi {
      font-size: 1.4rem;
    }
    .search-bar__file-browser-btn:hover {
      border-color: var(--fm-primary);
      color: var(--fm-primary);
    }
    .search-bar__file-browser-btn--active {
      background: var(--fm-primary);
      border-color: var(--fm-primary);
      color: #fff;
    }
    .search-bar__file-browser-btn--active:hover {
      color: #fff;
    }
  `],
})
export class SearchBarComponent implements OnInit {
  @Input() placeholder = 'Fájl keresése';
  @Input() debounceMs = 400;
  @Input() minLength = 3;
  @Input() showFileBrowserButton = false;
  @Input() fileBrowserActive = false;
  @Output() search = new EventEmitter<SearchQuery>();
  @Output() fileBrowserToggle = new EventEmitter<void>();

  searchText = '';
  deep = false;

  private readonly subject = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.subject
      .pipe(debounceTime(this.debounceMs), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((filter) => this.search.emit({ filter, deep: this.deep }));
  }

  onInput(): void {
    const trimmed = this.searchText.trim();
    if (trimmed.length > 0 && trimmed.length < this.minLength) return;
    this.subject.next(trimmed);
  }

  onDeepChange(): void {
    if (this.searchText.trim()) {
      this.search.emit({ filter: this.searchText.trim(), deep: this.deep });
    }
  }

  submit(): void {
    this.search.emit({ filter: this.searchText.trim(), deep: this.deep });
  }

  clear(): void {
    this.searchText = '';
    this.search.emit({ filter: '', deep: this.deep });
  }
}
